import { Entity } from "@common/types/entity";
import { fromIntToMonetary, fromMonetaryToInt } from "@common/helpers/monetary";
import { UpdateTransactionSplitV2Dto } from "@common/dto/transactions/update-transaction-split-v2.dto";
import {
    getEffectiveAccrualLedgerAccountId,
    getEffectiveGeneralLedgerAccountId,
} from "@common/helpers/transactions";
import { AccountingLedgerBasis } from "@common/types/domains/accounting/ledger";
import { TransactionBase } from "@common/types/transaction";
import {
    buildPrimaryLedgerOverride,
    findPrimaryLedgerId,
} from "@lib/accountOverrides";

export interface SplitRow {
    _id?: number;
    _isParent?: boolean;
    splitDescription?: string;
    amount?: number;
    /** Derived display field: percentage of the original transaction amount. */
    percentage?: number;
    categoryId?: string;
    entityId?: number;
    classIds?: string[];
    counterpartyId?: string | null;
    accountId?: string | null;
    accrualAccountId?: string | null;
    scheduleTemplateId?: string | null;
}

export type TransactionSplitFormRow = UpdateTransactionSplitV2Dto & {
    accrualAccountId?: string | null;
    scheduleTemplateId?: string | null;
};

export interface SplitLedger {
    id: string;
    basis: AccountingLedgerBasis;
    isPrimary?: boolean;
}

export type SplitLedgersByEntityId = Record<number, SplitLedger[]>;

interface ToSplitDtoParams {
    split: TransactionSplitFormRow;
    ledgers?: SplitLedger[];
    cashAccountBaseline?: {
        accountId: string | null | undefined;
    };
    accrualAccountBaseline?: {
        accountId: string | null | undefined;
    };
}

type SplitFormLedgerSource = Pick<
    TransactionBase,
    "amount" | "entityId" | "accountId" | "accrualAccountId" | "journalEntries"
>;

export function getSplitFormLedgerAccounts({
    transaction,
    ledgersByEntityId,
}: {
    transaction: SplitFormLedgerSource;
    ledgersByEntityId: SplitLedgersByEntityId;
}) {
    const ledgers = ledgersByEntityId[transaction.entityId] ?? [];
    const primaryLedgerIds = [
        AccountingLedgerBasis.cash,
        AccountingLedgerBasis.accruals,
    ].map((basis) => findPrimaryLedgerId(ledgers, basis));
    const primaryLedgerSource = {
        ...transaction,
        journalEntries: transaction.journalEntries?.filter((journalEntry) =>
            primaryLedgerIds.includes(journalEntry.accountingLedger.id),
        ),
    };

    return {
        accountId:
            getEffectiveGeneralLedgerAccountId(primaryLedgerSource) ??
            undefined,
        accrualAccountId:
            transaction.accrualAccountId ??
            getEffectiveAccrualLedgerAccountId(primaryLedgerSource) ??
            undefined,
    };
}

export function getSplitAccountBaselines({
    split,
    transaction,
    ledgersByEntityId,
}: {
    split: TransactionSplitFormRow;
    transaction: SplitFormLedgerSource & {
        splits?: Array<SplitFormLedgerSource & { id: number }> | null;
    };
    ledgersByEntityId: SplitLedgersByEntityId;
}): Pick<ToSplitDtoParams, "cashAccountBaseline" | "accrualAccountBaseline"> {
    const originalSplit = transaction.splits?.find(
        (candidate) => candidate.id === split.id,
    );
    const baseline = getSplitFormLedgerAccounts({
        transaction: originalSplit ?? transaction,
        ledgersByEntityId,
    });
    const isNewRow = split.id === undefined;

    return {
        cashAccountBaseline: { accountId: baseline.accountId },
        accrualAccountBaseline: isNewRow
            ? undefined
            : { accountId: baseline.accrualAccountId },
    };
}

const GRID_TEMPLATE_COLUMNS_WITH_CLASS =
    "40px 1fr 180px 220px 100px 150px 100px 220px 200px";
const GRID_TEMPLATE_COLUMNS_WITHOUT_CLASS =
    "40px 1fr 180px 220px 150px 100px 220px 200px";
// GL-first layouts: Category is removed and Cash ledger is moved to the
// slot right after Description (rownumber, description, ledger, counterparty,
// [class], amount, %, entity). Schedule appends after; the Accrual ledger
// track is inserted next to the Cash track because both ledger columns are
// rendered there.
const GRID_TEMPLATE_COLUMNS_WITH_CLASS_NO_CATEGORY =
    "40px 1fr 200px 180px 100px 150px 100px 220px";
const GRID_TEMPLATE_COLUMNS_WITHOUT_CLASS_NO_CATEGORY =
    "40px 1fr 200px 180px 150px 100px 220px";

const LEDGER_TRACK = "200px";
const SCHEDULE_TRACK = "200px";
// rownumber, description, cash ledger — the accrual track goes next.
const GL_FIRST_ACCRUAL_TRACK_INDEX = 3;

/**
 * Select the grid template that matches the currently visible columns. In
 * GL-first workspaces the Category column is hidden and Cash ledger is
 * promoted to the slot right after Description; the Class column is only
 * shown when classes are activated.
 */
export function getGridTemplateColumns(params: {
    hasClasses: boolean;
    isGlFirst: boolean;
    hasSchedule?: boolean;
    hasAccrualLedger?: boolean;
}): string {
    const {
        hasClasses,
        isGlFirst,
        hasSchedule = false,
        hasAccrualLedger = false,
    } = params;
    let base: string;
    if (isGlFirst) {
        base = hasClasses
            ? GRID_TEMPLATE_COLUMNS_WITH_CLASS_NO_CATEGORY
            : GRID_TEMPLATE_COLUMNS_WITHOUT_CLASS_NO_CATEGORY;
    } else {
        base = hasClasses
            ? GRID_TEMPLATE_COLUMNS_WITH_CLASS
            : GRID_TEMPLATE_COLUMNS_WITHOUT_CLASS;
    }
    const tracks = base.split(" ");
    if (hasAccrualLedger && isGlFirst) {
        tracks.splice(GL_FIRST_ACCRUAL_TRACK_INDEX, 0, LEDGER_TRACK);
    } else if (hasAccrualLedger) {
        tracks.push(LEDGER_TRACK);
    }
    if (hasSchedule) {
        tracks.push(SCHEDULE_TRACK);
    }
    return tracks.join(" ");
}

/**
 * Compute percentage (0–100, two decimal places) for a split amount relative
 * to the total original amount.  Returns 0 when originalAmount is zero to
 * avoid division by zero.
 *
 * Converts to integer cents first to avoid floating-point imprecision.
 */
export function amountToPercent(
    amount: number,
    originalAmount: number,
): number {
    if (originalAmount === 0) {
        return 0;
    }
    return (
        Math.round(
            (fromMonetaryToInt(amount) / fromMonetaryToInt(originalAmount)) *
                10000,
        ) / 100
    );
}

/**
 * Convert a percentage back to a monetary amount, rounding to cents.
 */
export function percentToAmount(
    percent: number,
    originalAmount: number,
): number {
    return fromIntToMonetary(
        Math.round((percent / 100) * fromMonetaryToInt(originalAmount)),
    );
}

export function areAmountsSymmetric(
    splits: UpdateTransactionSplitV2Dto[],
): boolean {
    const first = splits[0]?.amount;
    return splits.every((s) => s.amount === first);
}

/**
 * Recalculate the parent (remainder) row's amount so the total always equals
 * `originalAmount`.  Returns a new array with the parent row's amount updated.
 */
export function recalculateParentAmount(
    rows: SplitRow[],
    parentId: number,
    originalAmount: number,
): SplitRow[] {
    const parentIdx = rows.findIndex((r) => r._id === parentId);
    if (parentIdx === -1) {
        return rows;
    }
    const nonParentTotal = rows
        .filter((_, i) => i !== parentIdx)
        .reduce(
            (sum, r) => sum + fromMonetaryToInt((r.amount as number) ?? 0),
            0,
        );
    const parentAmount = fromIntToMonetary(
        fromMonetaryToInt(originalAmount) - nonParentTotal,
    );
    const result = [...rows];
    result[parentIdx] = { ...result[parentIdx], amount: parentAmount };
    return result;
}

/**
 * Recalculate the percentage field on every row based on its amount and the
 * original transaction amount.
 */
export function syncRowPercentages(
    rows: SplitRow[],
    originalAmount: number,
): SplitRow[] {
    return rows.map((r) => ({
        ...r,
        percentage: amountToPercent((r.amount as number) ?? 0, originalAmount),
    }));
}

/**
 * Convert internal `SplitRow[]` data to the DTO array expected by the API /
 * Formik state.  `parentSplit` supplies fallback values for entity, category,
 * etc. when a child row has no value.
 */
export function rowsToSplits(params: {
    rows: SplitRow[];
    parentSplit: TransactionSplitFormRow | undefined;
    entities: Array<Pick<Entity, "id" | "isPersonal">>;
}): TransactionSplitFormRow[] {
    const { rows, parentSplit, entities } = params;
    return rows.map((r) => {
        const entityId = r.entityId ?? parentSplit?.entityId ?? 0;
        const isPersonal = isPersonalEntityId({ entityId, entities });
        return {
            id: r._id,
            amount: r.amount ?? 0,
            entityId,
            splitDescription: r.splitDescription ?? "",
            categoryId: r.categoryId ?? parentSplit?.categoryId ?? "",
            classIds: r.classIds ?? [],
            counterpartyId: r.counterpartyId ?? undefined,
            accountId: isPersonal
                ? null
                : (r.accountId ?? parentSplit?.accountId ?? undefined),
            accrualAccountId: isPersonal
                ? null
                : (r.accrualAccountId ??
                  parentSplit?.accrualAccountId ??
                  undefined),
            scheduleTemplateId: r.scheduleTemplateId ?? undefined,
        };
    });
}

export function toSplitDto({
    split,
    ledgers = [],
    cashAccountBaseline,
    accrualAccountBaseline,
}: ToSplitDtoParams): UpdateTransactionSplitV2Dto {
    const accountId =
        cashAccountBaseline && cashAccountBaseline.accountId === split.accountId
            ? undefined
            : split.accountId;
    const accrualAccountId =
        accrualAccountBaseline &&
        accrualAccountBaseline.accountId === split.accrualAccountId
            ? undefined
            : split.accrualAccountId;
    const cashLedgerId = findPrimaryLedgerId(
        ledgers,
        AccountingLedgerBasis.cash,
    );
    const accrualLedgerId = findPrimaryLedgerId(
        ledgers,
        AccountingLedgerBasis.accruals,
    );
    const cashOverrides =
        accountId !== undefined
            ? buildPrimaryLedgerOverride(cashLedgerId, accountId)
            : [];
    const accrualOverrides =
        accrualAccountId !== undefined
            ? buildPrimaryLedgerOverride(accrualLedgerId, accrualAccountId)
            : [];
    const accountOverrides = [...cashOverrides, ...accrualOverrides];
    const hasResolvedLedgers = accountOverrides.length > 0;
    const isCashCarriedByOverride = cashOverrides.length > 0;

    return {
        id: split.id,
        entityId: split.entityId,
        amount: split.amount,
        splitDescription: split.splitDescription,
        categoryId: split.categoryId,
        classIds: split.classIds,
        counterpartyId: split.counterpartyId,
        accountId: isCashCarriedByOverride ? undefined : accountId,
        accountOverrides: hasResolvedLedgers
            ? accountOverrides
            : split.accountOverrides,
    };
}

export function syncCategoryToEntity(params: {
    row: SplitRow;
    entities: Array<Pick<Entity, "id" | "isPersonal" | "isBusiness">>;
    personalCategoryId: string;
    categoriesById: Record<string, { isBusiness?: boolean }>;
}): SplitRow {
    const { row, entities, personalCategoryId, categoriesById } = params;
    const entity = entities.find((e) => e.id === row.entityId);
    const currentCategory = row.categoryId
        ? categoriesById[row.categoryId]
        : undefined;
    const entityIsPersonal = !entity || entity.isPersonal;

    if (currentCategory && currentCategory.isBusiness === !entityIsPersonal) {
        return row;
    }

    if (entityIsPersonal) {
        return { ...row, categoryId: personalCategoryId };
    }

    if (currentCategory && !currentCategory.isBusiness) {
        return { ...row, categoryId: "" };
    }
    return row;
}

export function isPersonalEntityId(params: {
    entityId: number | undefined;
    entities: Array<Pick<Entity, "id" | "isPersonal">>;
}): boolean {
    const { entityId, entities } = params;
    return entities.some(
        (entity) => entity.id === entityId && entity.isPersonal,
    );
}

export function hasRowMissingCashAccount(params: {
    splits: Array<Pick<TransactionSplitFormRow, "entityId" | "accountId">>;
    entities: Array<Pick<Entity, "id" | "isPersonal">>;
}): boolean {
    const { splits, entities } = params;
    return splits.some(
        (split) =>
            !split.accountId &&
            !isPersonalEntityId({ entityId: split.entityId, entities }),
    );
}

/**
 * When a category changes, auto-switch the entity to match the category's
 * business/personal nature when the current entity conflicts.
 */
export function syncEntityToCategory(
    row: SplitRow,
    entities: Entity[],
    categoriesById: Record<string, { isBusiness?: boolean }>,
): SplitRow {
    if (!row.categoryId) {
        return row;
    }
    const category = categoriesById[row.categoryId];
    const entity = entities.find((e) => e.id === row.entityId);
    if (!category || !entity) {
        return row;
    }
    if (!category.isBusiness && entity.isBusiness) {
        const personal = entities.find((e) => e.isPersonal);
        return personal ? { ...row, entityId: personal.id } : row;
    }
    if (category.isBusiness && entity.isPersonal) {
        const business = entities.find((e) => e.isBusiness);
        return business ? { ...row, entityId: business.id } : row;
    }
    return row;
}
